// Linha de chips do cabecalho: status da loja, tempo de delivery, tempo de
// retirada e pedido minimo.
//
// Cada chip so aparece quando o dado existe — chip com "—" ocuparia espaco pra
// dizer que a loja nao configurou algo, o que nao interessa a quem vai pedir.

import { Bike, ShoppingBag, ShoppingCart } from "lucide-react";

import { proximaAbertura } from "../lib/businessHours";

export default function StoreChips({
  isOpen, closingTime, manuallyClosed, closeReason, nextOpenTime,
  schedulingActive, stats, darkMode, businessHours, onOpenHorarios, onOpenInfo,
  // "todas" = status + metricas na mesma linha (computador).
  // No celular o layout de referencia separa: as metricas ficam numa faixa
  // sozinha, logo abaixo do banner, e o status vai dentro do cartao.
  partes = "todas", className = "",
  // Mesa QR: tempo de entrega, de retirada e pedido minimo nao dizem nada pra
  // quem esta sentado na mesa — so' ocupam o topo do cardapio dele.
  modoMesa = false,
}) {
  const minimo = Number(stats?.pedido_minimo || 0);
  const temDelivery = stats?.delivery_fee_min != null;
  const tDelivery = stats?.avg_delivery_time_min;
  const tRetirada = stats?.avg_pickup_time_min;

  const mostraStatus = partes === "todas" || partes === "status";
  const naMesa = modoMesa || (typeof document !== "undefined" && document.body?.dataset?.modoMesa === "1");
  const mostraMetricas = !naMesa && (partes === "todas" || partes === "metricas");

  return (
    <div className={`flex items-center gap-1.5 lg:gap-2 flex-wrap ${className}`}>
      {mostraStatus && (
      <StatusChip
        isOpen={isOpen}
        closingTime={closingTime}
        manuallyClosed={manuallyClosed}
        closeReason={closeReason}
        nextOpenTime={nextOpenTime}
        schedulingActive={schedulingActive}
        businessHours={businessHours}
        onClick={onOpenHorarios}
      />
      )}

      {mostraMetricas && (
      <>
      {/* Dois tempos distintos, medidos de verdade nos ultimos 90 dias:
          DELIVERY conta ate o pedido ser concluido; RETIRADA, ate ficar pronto
          no balcao. Um numero so pra "tempo de entrega" enganava quem ia
          buscar, que espera muito menos que quem recebe em casa.
          O "~" e' proposital: e' media historica, nao promessa de entrega —
          numero seco vira expectativa exata e reclamacao quando nao cumpre. */}
      {temDelivery && tDelivery != null && (
        <Chip icon={Bike} tom="azul" darkMode={darkMode} onClick={onOpenInfo}
              title="Tempo médio de entrega nos últimos 90 dias">
          Delivery <strong className="font-bold">~{Math.max(1, tDelivery)} min</strong>
        </Chip>
      )}

      {tRetirada != null && (
        <Chip icon={ShoppingBag} tom="verde" darkMode={darkMode} onClick={onOpenInfo}
              title="Tempo médio de preparo nos últimos 90 dias">
          Retirada <strong className="font-bold">~{Math.max(1, tRetirada)} min</strong>
        </Chip>
      )}

      {minimo > 0 && (
        <Chip icon={ShoppingCart} tom="ambar" darkMode={darkMode} onClick={onOpenInfo}>
          {/* "Mín" no celular: a palavra inteira empurrava este chip pra uma
              terceira linha sozinho, e o cartao crescia por causa de 4 letras. */}
          <span className="lg:hidden">Mín</span>
          <span className="hidden lg:inline">Mínimo</span>
          {" R$ "}{minimo.toFixed(2).replace(".", ",")}
        </Chip>
      )}
      </>
      )}
    </div>
  );
}

/** Texto, cor e sufixo do status. Fora do componente pra manter o JSX legivel
 *  e o StoreChips dentro do limite de complexidade. */
function descreverStatus({
  isOpen, closingTime, manuallyClosed, closeReason, nextOpenTime,
  schedulingActive, businessHours,
}) {
  // `proximaAbertura` vem primeiro porque varre os proximos 7 dias; o
  // `nextOpenTime` da pagina so enxerga hoje e vem nulo depois do ultimo
  // fechamento — justamente quando o cliente mais precisa da informacao.
  const proxima = proximaAbertura(businessHours);
  const quando = proxima
    ? `abre ${proxima.quando} às ${proxima.hora}`
    : (nextOpenTime ? `abre às ${nextOpenTime}` : null);

  if (isOpen) {
    return {
      cor: "bg-green-500",
      classe: "text-green-600 dark:text-green-400",
      texto: "Aberto agora",
      // Sufixo so no computador: no celular ele empurrava o status pra duas linhas.
      sufixo: closingTime ? ` · fecha ${closingTime}` : null,
    };
  }

  if (schedulingActive) {
    return {
      cor: "bg-amber-500",
      classe: "text-amber-600 dark:text-amber-400",
      // "Agendamentos" sozinho nao dizia nada a quem chegou fora do horario.
      texto: quando ? `Agendamentos · ${quando}` : "Aceitando agendamentos",
      sufixo: null,
    };
  }

  let texto = "Fechado";
  if (quando) texto = `Fechado · ${quando}`;
  else if (manuallyClosed && closeReason) texto = `Fechado · ${closeReason}`;
  return { cor: "bg-red-500", classe: "text-red-600 dark:text-red-400", texto, sufixo: null };
}

function StatusChip(props) {
  const { manuallyClosed, closeReason, onClick } = props;
  const { cor, classe, texto, sufixo } = descreverStatus(props);
  // O motivo escrito pelo dono nao cabe junto do horario sem estourar a linha,
  // mas nao pode sumir — vai pro title.
  const titulo = manuallyClosed && closeReason
    ? `${closeReason} — ver horário de funcionamento`
    : "Ver horário de funcionamento";

  return (
    <button
      type="button"
      onClick={onClick}
      title={titulo}
      className={`inline-flex items-center gap-1.5 text-[10px] lg:text-sm font-semibold whitespace-nowrap ${classe} hover:opacity-80 transition-opacity`}
    >
      <span className={`w-2 h-2 rounded-full ${cor}`} />
      {texto}
      {sufixo && <span className="hidden lg:inline">{sufixo}</span>}
    </button>
  );
}


const TONS = {
  azul: {
    claro: "bg-blue-50 border-blue-200 text-blue-700",
    escuro: "bg-blue-500/10 border-blue-500/30 text-blue-300",
  },
  verde: {
    claro: "bg-emerald-50 border-emerald-200 text-emerald-700",
    escuro: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
  },
  ambar: {
    claro: "bg-amber-50 border-amber-200 text-amber-700",
    escuro: "bg-amber-500/10 border-amber-500/30 text-amber-300",
  },
};

function Chip({ icon: Icon, tom, darkMode, children, onClick, title }) {
  const cores = TONS[tom][darkMode ? "escuro" : "claro"];
  return (
    <button
      type="button"
      onClick={onClick}
      title={title || "Ver informações da loja"}
      // Chip bem compacto no celular: com o tamanho do desktop cada um ocupava
      // ~125px num cartao de 242px, entao cabia UM por linha e a coluna de
      // chips virava quatro linhas. Nesse tamanho cabem dois por linha.
      className={`inline-flex items-center gap-1 lg:gap-1.5 px-1.5 lg:px-2.5 py-0.5 lg:py-1 rounded-lg border text-[9px] lg:text-xs font-medium whitespace-nowrap ${cores} hover:opacity-80 transition-opacity`}
    >
      <Icon className="w-3 h-3 lg:w-3.5 lg:h-3.5" strokeWidth={2} />
      {children}
    </button>
  );
}
